import { useState, useCallback, useRef } from 'react';
import api from '../api';
import logger from '../utils/logger';
import toastService from '../services/toastService';
import { getApiErrorMessage, isMembershipRequiredError } from '../utils/membershipAccess';

const SESSION_POPUP_DISMISSED = 'contribution_popup_dismissed_session';

/**
 * useSearchActions - Manages all search-related API calls and actions
 * Handles search execution, data loading, and user interactions
 */
export const useSearchActions = (searchState, userState, filterState) => {
  const {
    users, setUsers,
    searchCriteria,
    loading, setLoading,
    loadingMore, setLoadingMore,
    loadingStartTime, setLoadingStartTime,
    elapsedTime, setElapsedTime,
    initialSearchComplete, setInitialSearchComplete,
    error, setError,
    currentPage, setCurrentPage,
    totalResults, setTotalResults,
    hasMoreResults, setHasMoreResults,
    sortBy, setSortBy,
    sortOrder, setSortOrder,
    hasAutoExecutedRef,
    searchResultsRef,
    loadMoreTriggerRef,
    loadingPageRef,
    searchAbortRef,
    accumulatedCountRef,
    resetSearchState,
    startLoadingTimer,
    updateElapsedTime,
    selectedProfileForDetail, setSelectedProfileForDetail,
    excludedProfileMessage, setExcludedProfileMessage,
    excludedProfileId, setExcludedProfileId,
    excludedProfileUsername, setExcludedProfileUsername,
  } = searchState;

  const {
    currentUserProfile,
    favoritedUsers, setFavoritedUsers,
    shortlistedUsers, setShortlistedUsers,
    excludedUsers, setExcludedUsers,
  } = userState;

  const {
    loadSavedSearches,
    loadOccupationOptions,
    loadLocationOptions,
  } = filterState;

  // ===== DATA LOADING FUNCTIONS =====

  // Load all initial data in coordinated way to prevent race conditions
  const loadAllInitialData = useCallback(async () => {
    const currentUser = localStorage.getItem('username');
    try {
      // Load user profile and data in parallel
      const [profileResponse, userDataResponse] = await Promise.all([
        api.get(`/profile/${currentUser}?requester=${currentUser}`).catch(err => {
          logger.error('❌ Error loading profile:', err);
          return { data: null }; // Fallback response
        }),
        Promise.all([
          api.get(`/favorites/${currentUser}`).catch(err => {
            logger.error('❌ Error loading favorites:', err);
            return { data: [] }; // Fallback empty array
          }),
          api.get(`/shortlist/${currentUser}`).catch(err => {
            logger.error('❌ Error loading shortlist:', err);
            return { data: [] }; // Fallback empty array
          }),
          api.get(`/exclusions/${currentUser}`).catch(err => {
            logger.error('❌ Error loading exclusions:', err);
            return { data: [] }; // Fallback empty array
          })
        ]).catch(err => {
          logger.error('❌ Error loading user data batch:', err);
          return [[], [], []]; // Fallback empty arrays
        })
      ]);
      
      // Process profile data
      if (profileResponse?.data) {
        // Profile processing would be handled by parent component
      }
      
      // Process user data
      const [favResponse, shortlistResponse, exclusionsResponse] = userDataResponse;
      
      // APIs return nested objects: {favorites: [...]}, {shortlist: [...]}, {exclusions: [...]}
      const favData = favResponse?.data?.favorites || favResponse?.data || [];
      const shortlistData = shortlistResponse?.data?.shortlist || shortlistResponse?.data || [];
      const exclusionsData = exclusionsResponse?.data?.exclusions || exclusionsResponse?.data || [];
      
      const favArray = Array.isArray(favData) ? favData : [];
      const shortlistArray = Array.isArray(shortlistData) ? shortlistData : [];
      const exclusionsArray = Array.isArray(exclusionsData) ? exclusionsData : [];
      
      const favoriteUsernames = favArray.map(fav => fav.targetUsername || fav.favoriteUsername || fav.username);
      const shortlistUsernames = shortlistArray.map(sl => sl.targetUsername || sl.username);
      const exclusionUsernames = exclusionsArray.map(ex => ex.targetUsername || ex.username);
      
      setFavoritedUsers(new Set(favoriteUsernames));
      setShortlistedUsers(new Set(shortlistUsernames));
      setExcludedUsers(new Set(exclusionUsernames));
      
      logger.info('✅ Loaded user interactions:', {
        favorites: favoriteUsernames.length,
        shortlist: shortlistUsernames.length,
        exclusions: exclusionUsernames.length
      });
      
    } catch (err) {
      logger.error('❌ Error loading initial data:', err);
    }
  }, [setFavoritedUsers, setShortlistedUsers, setExcludedUsers]);

  // Load other data that doesn't depend on profile
  const loadOtherData = useCallback(async () => {
    try {
      await Promise.all([
        loadSavedSearches(),
        loadOccupationOptions(),
        loadLocationOptions()
      ]);
    } catch (err) {
      logger.error('❌ Error loading other data:', err);
    }
  }, [loadSavedSearches, loadOccupationOptions, loadLocationOptions]);

  // Load online users function
  const loadOnlineUsers = useCallback(async (setOnlineUsers) => {
    try {
      const response = await api.get('/online-status/users');
      logger.debug('Loaded online users:', response.data.onlineUsers);
      setOnlineUsers(new Set(response.data.onlineUsers || []));
    } catch (err) {
      logger.error('❌ Error loading online users:', err);
      setOnlineUsers(new Set());
    }
  }, []);

  // Load PII requests
  const loadPiiRequests = useCallback(async (setPiiRequests) => {
    const currentUser = localStorage.getItem('username');
    if (!currentUser) return;

    try {
      // Load both outgoing requests AND received access grants
      const [requestsResponse, accessResponse] = await Promise.all([
        api.get(`/pii-requests/${currentUser}/outgoing`),
        api.get(`/pii-access/${currentUser}/received`)
      ]);

      const requests = requestsResponse.data.requests || [];
      const receivedAccess = accessResponse.data.receivedAccess || [];
      const requestStatus = {};
      
      logger.info('🔍 PII API Responses:');
      logger.info('  Requests:', requests);
      logger.info('  Received Access:', receivedAccess);

      // First, add ONLY pending requests (not approved ones - those must be in receivedAccess)
      requests.forEach(req => {
        // Handle different response formats
        const targetUsername = req.profileUsername || req.requestedUsername || req.profileOwner?.username;
        if (targetUsername && req.requestType && req.status === 'pending') {
          requestStatus[`${targetUsername}_${req.requestType}`] = 'pending';
        }
      });

      // Then, add all received ACTIVE access (these are truly approved grants)
      receivedAccess.forEach(access => {
        const targetUsername = access?.userProfile?.username;
        if (targetUsername && access?.accessTypes) {
          // Mark all access types as 'approved' ONLY if in receivedAccess (isActive: true)
          access.accessTypes.forEach(accessType => {
            requestStatus[`${targetUsername}_${accessType}`] = 'approved';
          });
        }
      });

      logger.info('📊 PII Access Status:', requestStatus);
      setPiiRequests(requestStatus);
    } catch (err) {
      logger.error('❌ Error loading PII requests:', err);
      setPiiRequests({});
    }
  }, []);

  // ===== SEARCH FUNCTIONS =====

  // Criteria/sort that produced the currently displayed result set (page 1).
  // Pages 2+ must reuse these — NOT live form state — so edited-but-unsubmitted
  // filters can't mix a different query into existing results while
  // totalResults stays pinned to the page-1 response.
  const executedQueryRef = useRef(null);

  // Main search function
  const handleSearch = useCallback(async (page = 1, overrideCriteria = null, overrideSort = null, options = {}) => {
    
    // Cancel any in-flight search request to prevent stale data from overwriting
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    try {
      setLoading(true);
      startLoadingTimer();

      // Membership access is enforced by the backend; a 403 surfaces via
      // isMembershipRequiredError below and triggers the contribution popup.
      if (page === 1) {
        resetSearchState();
        loadingPageRef.current = 1; // Reset page tracking ref for new search
      }

      // STEP 1: Apply traditional search filters
      const executedQuery = page > 1 ? executedQueryRef.current : null;
      const criteriaToUse = overrideCriteria || executedQuery?.criteria || searchCriteria;
      const sortByToUse = overrideSort?.sortBy || executedQuery?.sortBy || sortBy;
      const sortOrderToUse = overrideSort?.sortOrder || executedQuery?.sortOrder || sortOrder;

      if (page === 1) {
        executedQueryRef.current = {
          criteria: criteriaToUse,
          sortBy: sortByToUse,
          sortOrder: sortOrderToUse
        };
      }

      // Build search query
      const query = new URLSearchParams();
      
      // Convert height feet/inches to total inches before sending to API
      const criteriaWithHeightInches = { ...criteriaToUse };

      // Keep sorting sourced only from dedicated sort state/override.
      // Some legacy saved criteria payloads may still include sortBy/sortOrder.
      delete criteriaWithHeightInches.sortBy;
      delete criteriaWithHeightInches.sortOrder;
      
      // Calculate heightMin in total inches (feet * 12 + inches)
      if (criteriaWithHeightInches.heightMinFeet && criteriaWithHeightInches.heightMinInches !== undefined) {
        const feet = parseInt(criteriaWithHeightInches.heightMinFeet) || 0;
        const inches = parseInt(criteriaWithHeightInches.heightMinInches) || 0;
        criteriaWithHeightInches.heightMin = feet * 12 + inches;
      }
      
      // Calculate heightMax in total inches (feet * 12 + inches)
      if (criteriaWithHeightInches.heightMaxFeet && criteriaWithHeightInches.heightMaxInches !== undefined) {
        const feet = parseInt(criteriaWithHeightInches.heightMaxFeet) || 0;
        const inches = parseInt(criteriaWithHeightInches.heightMaxInches) || 0;
        criteriaWithHeightInches.heightMax = feet * 12 + inches;
      }
      
      // Remove feet/inches fields - backend doesn't need them
      delete criteriaWithHeightInches.heightMinFeet;
      delete criteriaWithHeightInches.heightMinInches;
      delete criteriaWithHeightInches.heightMaxFeet;
      delete criteriaWithHeightInches.heightMaxInches;

      // Prefer modern multi-select locations over legacy single location.
      if (Array.isArray(criteriaWithHeightInches.locations) && criteriaWithHeightInches.locations.length > 0) {
        delete criteriaWithHeightInches.location;
      }
      
      // Add basic criteria
      Object.entries(criteriaWithHeightInches).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => query.append(key, v));
          } else {
            query.append(key, value);
          }
        }
      });

      // Add pagination and sorting
      query.append('page', page);
      query.append('limit', '24');
      // Pages 2+ reuse the page-1 total (pinned in executedQueryRef), so skip
      // the $count over the whole match set — hasMore comes from limit+1.
      if (page > 1) {
        query.append('skipTotalCount', 'true');
      }
      query.append('sortBy', sortByToUse);
      query.append('sortOrder', sortOrderToUse);

      logger.info(`🔍 Searching with criteria:`, Object.fromEntries(query));
      
      const response = await api.get(`/search?${query.toString()}`, {
        signal: abortController.signal
      });

      const {
        users: newUsers,
        total,
        hasMore: serverHasMore,
        serverReturnedFullPage,
        excludedProfileMessage: excludedMsg,
        excludedProfileId: excludedId,
        excludedProfileUsername: excludedUsername
      } = response.data;

      // Backend emits `hasMore`; keep the legacy key as fallback.
      const resolvedHasMore = serverHasMore ?? serverReturnedFullPage ?? false;

      if (page === 1) {
        setUsers(newUsers || []);
        setCurrentPage(1);
        setTotalResults(total || 0);
        setHasMoreResults(resolvedHasMore);
        
        // Set excluded profile data if present
        setExcludedProfileMessage(excludedMsg || null);
        setExcludedProfileId(excludedId || null);
        setExcludedProfileUsername(excludedUsername || null);
        
        // Auto-select first profile in split view so right panel isn't blank
        if (selectedProfileForDetail && newUsers.length > 0) {
          setSelectedProfileForDetail(newUsers[0]);
        }
      } else {
        // Subsequent pages: append new users and calculate hasMore
        setUsers(prev => {
          const existingUsernames = new Set(prev.map(u => u.username));
          const newUsersFiltered = newUsers.filter(u => !existingUsernames.has(u.username));
          const newTotal = prev.length + newUsersFiltered.length;
          
          // Update ref for tracking
          accumulatedCountRef.current = newTotal;
          
          return [...prev, ...newUsersFiltered];
        });
        
        setCurrentPage(page);
        setHasMoreResults(resolvedHasMore);
      }

      setInitialSearchComplete(true);
      setError('');
      
      logger.info(`✅ Search completed - found ${newUsers?.length || 0} users (total: ${total})`);
      return true;
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.error('❌ Search error:', err);
        
        // Check if error is due to membership requirement
        if (isMembershipRequiredError(err)) {
          // Only open the activation popup for user-initiated searches —
          // page-load bootstrap errors just render the lock gate (its
          // "Activate membership" CTA opens the same popup on click).
          logger.info('🔔 Membership required for search');
          if (options.userInitiated !== false && !sessionStorage.getItem(SESSION_POPUP_DISMISSED)) {
            window.dispatchEvent(new CustomEvent('force-contribution-popup'));
          }
          setError('Membership required for search. Please complete your membership payment.');
        } else {
          setError(getApiErrorMessage(err, 'Search failed'));
        }
        
        // Mark the search attempt as complete so the UI can render feedback
        // (empty state / error) instead of staying blank forever.
        setInitialSearchComplete(true);
      }
      return false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      searchAbortRef.current = null;
    }
  }, [
    searchCriteria, sortBy, sortOrder,
    resetSearchState, startLoadingTimer,
    setUsers, setCurrentPage, setTotalResults, setHasMoreResults,
    setInitialSearchComplete, setError, setLoading, setLoadingMore,
    searchAbortRef, loadingPageRef, accumulatedCountRef,
    selectedProfileForDetail, setSelectedProfileForDetail
  ]);

  // ===== SAVED SEARCHES =====

  // Save search
  const handleSaveSearch = useCallback(async (saveData) => {
    try {
      const username = localStorage.getItem('username');
      if (!username) {
        toastService.error('Please login to save searches');
        return;
      }
      
      const searchName = typeof saveData === 'string' ? saveData : saveData.name;
      const notifications = typeof saveData === 'object' ? saveData.notifications : null;
      
      const searchData = {
        name: searchName.trim(),
        criteria: searchCriteria,
        minMatchScore: 0,
        created_at: new Date().toISOString(),
        ...(notifications && { notifications })
      };
      
      await api.post(`/${username}/saved-searches`, searchData);
      toastService.success(`✅ Search saved: "${searchName}"`);
      loadSavedSearches();
    } catch (err) {
      logger.error('Error saving search:', err);
      toastService.error('Failed to save search. ' + (err.response?.data?.detail || err.message));
    }
  }, [searchCriteria, loadSavedSearches]);

  // ===== USER INTERACTIONS =====

  // Toggle list membership (favorites/shortlist/exclusions)
  const toggleListAction = useCallback(async (listName, apiPath, usersSet, setUsersSet, targetUsername) => {
    const isCurrentlyInList = usersSet.has(targetUsername);
    try {
      if (isCurrentlyInList) {
        await api.delete(apiPath);
        setUsersSet(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUsername);
          return newSet;
        });
        toastService.success(`Removed from ${listName}`);
      } else {
        await api.post(apiPath);
        setUsersSet(prev => new Set([...prev, targetUsername]));
        toastService.success(`Added to ${listName}`);
      }
    } catch (err) {
      logger.error(`Error toggling ${listName}:`, err);
      if (err.response?.status === 409) {
        toastService.info(`Already in ${listName}`);
      } else {
        toastService.error(`Failed to update ${listName}: ${err.response?.data?.detail || err.message}`);
      }
    }
  }, []);

  // Handle profile actions (favorite, shortlist, exclude, message)
  const handleProfileAction = useCallback(async (e, targetUsername, action) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    const currentUser = localStorage.getItem('username');
    if (!currentUser) {
      toastService.error('Please login to perform this action');
      return;
    }

    if (targetUsername === currentUser) {
      toastService.error('You cannot perform this action on your own profile');
      return;
    }

    switch (action) {
      case 'favorite':
      case 'unfavorite':
        await toggleListAction(
          'favorites',
          `/favorites/${targetUsername}?username=${currentUser}`,
          favoritedUsers,
          setFavoritedUsers,
          targetUsername
        );
        break;
        
      case 'shortlist':
      case 'unshortlist':
        await toggleListAction(
          'shortlist',
          `/shortlist/${targetUsername}?username=${currentUser}`,
          shortlistedUsers,
          setShortlistedUsers,
          targetUsername
        );
        break;
        
      case 'exclude':
      case 'unexclude':
        // For exclude action, we need to trigger the exclusion preview workflow
        // This is handled by the parent component, not directly in the hook
        // Return a special indicator to parent to handle exclusion workflow
        return { type: 'exclude', targetUsername };
        
      case 'message':
        // For message action, we need to trigger the message modal
        // This is handled by the parent component
        return { type: 'message', targetUsername };
        
      default:
        logger.error('Unknown profile action:', action);
        toastService.error('Unknown action');
    }
  }, [
    favoritedUsers, setFavoritedUsers,
    shortlistedUsers, setShortlistedUsers,
    toggleListAction
  ]);

  // ===== PII REQUESTS =====

  // Open PII request modal
  const actuallyOpenPIIRequestModal = useCallback(async (user) => {
    const targetUsername = user.username;

    try {
      const currentUser = localStorage.getItem('username');
      const response = await api.post(`/pii-requests/${currentUser}/${targetUsername}`, {
        requestType: 'contact_info'
      });
      
      logger.info('PII request sent:', response.data);
      toastService.success('📨 PII request sent successfully');
    } catch (err) {
      logger.error('Error sending PII request:', err);
      toastService.error('Failed to send PII request');
    }
  }, []);

  // Handle PII request success
  const handlePIIRequestSuccess = useCallback(async () => {
    // Reload PII requests to get updated status
    await loadPiiRequests();
  }, [loadPiiRequests]);

  // ===== UTILITY FUNCTIONS =====

  // Check PII access
  const hasPiiAccess = useCallback((targetUsername, requestType) => {
    // This would check against piiRequests state
    return false; // Placeholder
  }, []);

  return {
    // Data loading
    loadAllInitialData,
    loadOtherData,
    loadOnlineUsers,
    loadPiiRequests,
    
    // Search functions
    handleSearch,
    
    // Saved searches
    handleSaveSearch,
    
    // User interactions
    handleProfileAction,
    toggleListAction,
    
    // PII requests
    actuallyOpenPIIRequestModal,
    handlePIIRequestSuccess,
    hasPiiAccess,
  };
};
